import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Store as StoreIcon,
  Warehouse as WarehouseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';

export type LocationScopeOption = {
  id: number;
  name: string;
};

export type LocationScopeControlProps = {
  locations?: LocationScopeOption[];
  /** Current location id as string; empty or `"0"` means all (when unlocked). */
  value?: string;
  locked?: boolean;
  disabled?: boolean;
  onChange?: (next: { locationId: string; locationName: string }) => void;
  /** Etiqueta fija (p. ej. "Almacén"): siempre bloqueado, sin menú. */
  fixedLabel?: string;
  /** Tooltip cuando está bloqueado. */
  lockedTooltip?: string;
  /** Icono del chip. */
  iconVariant?: 'store' | 'warehouse';
};

const ScopeRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'locked' && prop !== 'interactive',
})<{ locked?: boolean; interactive?: boolean }>(({ theme, locked, interactive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 160,
  maxWidth: 240,
  height: 40,
  padding: theme.spacing(0, 1.75),
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  backgroundColor: theme.palette.background.paper,
  color: locked ? theme.palette.text.secondary : theme.palette.text.primary,
  cursor: interactive ? 'pointer' : 'default',
  border: '1px solid transparent',
  backgroundImage: locked
    ? `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(to right, ${alpha('#06b6d4', 0.35)}, ${alpha('#10b981', 0.35)})`
    : `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(to right, #06b6d4, #10b981)`,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  transition: 'all 0.2s ease-in-out',
  ...(interactive
    ? {
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 18px rgba(6, 182, 212, 0.18)',
        },
      }
    : {}),
  '&.Mui-disabled': {
    opacity: 1,
  },
}));

const ALL_LOCATIONS_ID = 0;

/**
 * Alcance de ubicación en la toolbar (entre Reiniciar y Buscar).
 * Sucursal: bloqueado o menú. Almacén: etiqueta fija bloqueada.
 */
const LocationScopeControl: React.FC<LocationScopeControlProps> = ({
  locations = [],
  value = '',
  locked: lockedProp = false,
  disabled = false,
  onChange,
  fixedLabel,
  lockedTooltip,
  iconVariant = 'store',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const locked = Boolean(fixedLabel) || lockedProp;
  const interactive = !locked && !disabled && Boolean(onChange);
  const open = Boolean(anchorEl) && interactive;
  const ScopeIcon = iconVariant === 'warehouse' ? WarehouseIcon : StoreIcon;
  const displayName = fixedLabel
    ? fixedLabel
    : !value || value === '0'
      ? locked
        ? 'Sucursal'
        : 'Todas las sucursales'
      : (locations.find((loc) => loc.id.toString() === value)?.name ?? 'Sucursal');

  const menuOptions = useMemo(() => {
    const branches = locations.filter((loc) => loc.id !== ALL_LOCATIONS_ID);
    return [{ id: ALL_LOCATIONS_ID, name: 'Todas las sucursales' }, ...branches];
  }, [locations]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (option: LocationScopeOption) => {
    handleClose();
    const nextId = option.id === ALL_LOCATIONS_ID ? '' : option.id.toString();
    if (nextId === (value === '0' ? '' : value)) return;
    onChange?.({ locationId: nextId, locationName: option.name });
  };

  const tooltipTitle =
    lockedTooltip ?? (fixedLabel ? 'Solo inventario de almacén' : 'Tu sucursal asignada');

  const control = (
    <ScopeRoot
      locked={locked}
      interactive={interactive}
      onClick={handleOpen}
      disabled={disabled}
      aria-haspopup={interactive ? 'listbox' : undefined}
      aria-expanded={open ? 'true' : undefined}
      aria-label={locked ? `Alcance: ${displayName}` : 'Cambiar sucursal'}
    >
      <ScopeIcon
        sx={{
          fontSize: '1.2rem',
          color: locked ? 'grey.500' : 'grey.600',
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        noWrap
        sx={{
          fontWeight: 600,
          fontSize: '0.875rem',
          color: locked ? 'text.secondary' : 'text.primary',
          flex: 1,
          textAlign: 'left',
          minWidth: 0,
        }}
      >
        {displayName}
      </Typography>
      {interactive ? (
        <ArrowDownIcon
          sx={{
            fontSize: '1.15rem',
            color: 'grey.500',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      ) : null}
    </ScopeRoot>
  );

  return (
    <Box sx={{ flexShrink: 0 }}>
      {locked ? (
        <Tooltip title={tooltipTitle} arrow>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {control}
          </Box>
        </Tooltip>
      ) : (
        control
      )}

      {!fixedLabel ? (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1,
              minWidth: 220,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            },
          }}
          MenuListProps={{ dense: true, role: 'listbox' }}
        >
          {menuOptions.map((option) => {
            const isSelected =
              option.id === ALL_LOCATIONS_ID
                ? !value || value === '0'
                : option.id.toString() === value;
            return (
              <MenuItem
                key={option.id}
                selected={isSelected}
                onClick={() => handleSelect(option)}
                sx={{
                  py: 1.1,
                  '&.Mui-selected': {
                    backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isSelected ? (
                    <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <StoreIcon fontSize="small" sx={{ color: 'grey.400' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={option.name}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: '0.875rem',
                  }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </Box>
  );
};

export default LocationScopeControl;
